import React, { useRef, useState } from "react";
import { MdCameraAlt, MdDelete, MdDownload } from "react-icons/md";
import { buildPowerBiDashboardUrl } from "../../utils/powerbi";
import { alertDialog } from "../../utils/dialogs";
import { DashboardIcon } from "../shared/icons/Icons";

function SystemReports() {
  const [snapshots, setSnapshots] = useState([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showSnapshotTray, setShowSnapshotTray] = useState(false);
  const dashboardFrameRef = useRef(null);

  const systemReportUrl = buildPowerBiDashboardUrl(
    "https://app.powerbi.com/reportEmbed?reportId=d19cea58-4d3d-4dcd-a844-2e4be8913ea6&autoAuth=true&ctid=e8479183-da5c-4666-910d-38afe2ff031b",
  );

  const escapeHtml = (value = "") =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const handleTakeSnapshot = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      alertDialog(
        "Screen capture is not supported in this browser. Please use Chrome or Edge on localhost/HTTPS.",
        { title: "Screen Capture Unsupported" },
      );
      return;
    }

    setIsCapturing(true);

    try {
      dashboardFrameRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      await new Promise((resolve) => setTimeout(resolve, 450));

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: "always",
          displaySurface: "browser",
        },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: "include",
        surfaceSwitching: "exclude",
        systemAudio: "exclude",
      });

      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;

      await new Promise((resolve) => {
        video.onloadedmetadata = resolve;
      });
      await video.play();
      await new Promise((resolve) => setTimeout(resolve, 500));

      const dashboardRect = dashboardFrameRef.current?.getBoundingClientRect();
      const scaleX = video.videoWidth / window.innerWidth;
      const scaleY = video.videoHeight / window.innerHeight;
      const sourceX = dashboardRect ? Math.max(0, dashboardRect.left * scaleX) : 0;
      const sourceY = dashboardRect ? Math.max(0, dashboardRect.top * scaleY) : 0;
      const sourceWidth = dashboardRect
        ? Math.min(video.videoWidth - sourceX, dashboardRect.width * scaleX)
        : video.videoWidth;
      const sourceHeight = dashboardRect
        ? Math.min(video.videoHeight - sourceY, dashboardRect.height * scaleY)
        : video.videoHeight;

      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(sourceWidth));
      canvas.height = Math.max(1, Math.round(sourceHeight));
      const context = canvas.getContext("2d");
      context.drawImage(
        video,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        canvas.width,
        canvas.height,
      );

      stream.getTracks().forEach((track) => track.stop());

      setSnapshots((prev) => [
        ...prev,
        {
          id: Date.now(),
          capturedAt: new Date().toLocaleString(),
          dataUrl: canvas.toDataURL("image/jpeg", 0.92),
        },
      ]);
      setShowSnapshotTray(true);
    } catch (error) {
      if (error?.name !== "NotAllowedError") {
        console.error("Failed to capture system dashboard snapshot", error);
        alertDialog("Could not capture the dashboard snapshot.", { title: "Snapshot Failed" });
      }
    } finally {
      setIsCapturing(false);
    }
  };

  const handleRemoveSnapshot = (snapshotId) => {
    setSnapshots((prev) => prev.filter((snapshot) => snapshot.id !== snapshotId));
  };

  const handleDownloadReport = () => {
    if (snapshots.length === 0) {
      alertDialog("Take at least one dashboard snapshot before downloading.", { title: "No Snapshots" });
      return;
    }

    const reportWindow = window.open("", "_blank");
    if (!reportWindow) {
      alertDialog("Please allow pop-ups for this site to create the PDF report.", { title: "Pop-up Blocked" });
      return;
    }

    reportWindow.opener = null;

    const logoUrl = `${window.location.origin}${process.env.PUBLIC_URL}/als-logo.png`;
    const generatedAt = new Date().toLocaleString();
    const reportTitle = "System Analytics Report";
    const snapshotsMarkup = snapshots
      .map(
        (snapshot, index) => `
          <section class="snapshot-card">
            <div class="snapshot-heading">
              <h3>Snapshot ${index + 1}</h3>
              <span>${escapeHtml(snapshot.capturedAt)}</span>
            </div>
            <img src="${snapshot.dataUrl}" alt="Dashboard snapshot ${index + 1}" />
          </section>
        `,
      )
      .join("");

    reportWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${reportTitle}</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 0;
              padding: 18px 28px;
              background: #ffffff;
              color: #0f1d36;
              font-family: "Segoe UI", Arial, sans-serif;
            }
            .topline {
              display: grid;
              grid-template-columns: 1fr auto 1fr;
              align-items: center;
              gap: 12px;
              font-size: 10px;
              font-weight: 700;
              color: #0f1d36;
            }
            .topline span:nth-child(2) { text-align: center; }
            .topline span:last-child { text-align: right; }
            .red-rule {
              height: 6px;
              margin: 7px 0 22px;
              background: #d4272e;
            }
            .report-shell {
              max-width: 1120px;
              margin: 0 auto;
              background: #ffffff;
            }
            .report-header {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 20px;
              padding: 0 42px 24px;
              border-bottom: 1px solid #dbe3ef;
            }
            .brand {
              display: flex;
              align-items: center;
              gap: 26px;
            }
            .brand img {
              width: 118px;
              height: 74px;
              object-fit: contain;
              background: #ffffff;
            }
            .report-title-kicker {
              color: #d4272e;
              font-size: 12px;
              font-weight: 900;
              letter-spacing: 3px;
              text-transform: uppercase;
            }
            .brand h1,
            .section-title {
              margin: 0;
              color: #0f1d36;
              font-family: Georgia, "Times New Roman", serif;
              font-size: 42px;
              line-height: 0.96;
            }
            .report-meta {
              text-align: right;
              color: #0f1d36;
              font-size: 14px;
              line-height: 1.8;
              padding-top: 22px;
            }
            .report-meta strong { font-weight: 900; }
            .report-info-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 18px;
              padding: 28px 42px;
              border-bottom: 1px solid #e5e7eb;
            }
            .report-info-card {
              min-height: 86px;
              padding: 18px 20px;
              border: 1px solid #d4dfec;
              border-radius: 8px;
              background: #ffffff;
            }
            .report-info-card span {
              display: block;
              margin-bottom: 10px;
              color: #0f1d36;
              font-size: 11px;
              font-weight: 900;
              letter-spacing: 1.5px;
              text-transform: uppercase;
            }
            .report-info-card strong {
              color: #0f1d36;
              font-size: 16px;
            }
            .snapshots-section {
              padding: 24px 42px 36px;
            }
            .section-title {
              margin-bottom: 18px;
              font-size: 28px;
              line-height: 1.2;
            }
            .snapshot-card {
              padding: 18px 22px 22px;
              border: 1px solid #d4dfec;
              border-radius: 10px;
              margin-bottom: 28px;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .snapshot-heading {
              display: flex;
              justify-content: space-between;
              gap: 18px;
              align-items: center;
              margin-bottom: 14px;
            }
            .snapshot-heading h3 {
              margin: 0;
              color: #0f1d36;
              font-family: Georgia, "Times New Roman", serif;
              font-size: 22px;
            }
            .snapshot-heading span {
              color: #0f1d36;
              font-size: 12px;
              font-weight: 800;
            }
            .snapshot-card img {
              width: 100%;
              display: block;
              border: 1px solid #d4dfec;
              border-radius: 8px;
            }
            @media print {
              body {
                padding: 0;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              @page { margin: 14mm 12mm; }
              .snapshot-card { margin-bottom: 24px; }
            }
          </style>
        </head>
        <body>
          <main class="report-shell">
            <div class="topline">
              <span>${escapeHtml(generatedAt)}</span>
              <span>${reportTitle}</span>
              <span></span>
            </div>
            <div class="red-rule"></div>
            <header class="report-header">
              <div class="brand">
                <img src="${logoUrl}" alt="American Lycetuff" />
                <div>
                  <div class="report-title-kicker">American Lycetuff</div>
                  <h1>System<br />Analytics<br />Report</h1>
                </div>
              </div>
              <div class="report-meta">
                <div><strong>Scope:</strong> All Campuses</div>
                <div><strong>Generated:</strong> ${escapeHtml(generatedAt)}</div>
              </div>
            </header>
            <section class="report-info-grid">
              <div class="report-info-card">
                <span>Report Scope</span>
                <strong>System Wide</strong>
              </div>
              <div class="report-info-card">
                <span>Dashboard</span>
                <strong>Power BI System Report</strong>
              </div>
              <div class="report-info-card">
                <span>Snapshots</span>
                <strong>${snapshots.length}</strong>
              </div>
            </section>
            <section class="snapshots-section">
              <h2 class="section-title">Power BI Dashboard Snapshots</h2>
              ${snapshotsMarkup}
            </section>
          </main>
          <script>
            window.addEventListener("load", function () {
              setTimeout(function () { window.print(); }, 900);
            });
          </script>
        </body>
      </html>
    `);
    reportWindow.document.close();
  };

  return (
    <div className="dashboard-content system-report-page">
      <div className="powerbi-report-hero">
        <div>
          <span className="powerbi-kicker">Power BI Supported Insights</span>
          <h2>System-Wide Analytics Dashboard</h2>
        </div>
        <div className="system-report-actions">
          <button
            type="button"
            className="analytics-download-btn system-report-action-btn"
            onClick={handleTakeSnapshot}
            disabled={isCapturing}
          >
            <MdCameraAlt /> {isCapturing ? "Capturing..." : "Take Snapshot"}
          </button>
          <button
            type="button"
            className="analytics-download-btn system-report-action-btn"
            onClick={handleDownloadReport}
            disabled={snapshots.length === 0}
          >
            <MdDownload /> Done & Download PDF
          </button>
          {snapshots.length > 0 && (
            <button
              type="button"
              className="analytics-download-btn system-report-action-btn"
              onClick={() => setShowSnapshotTray((prev) => !prev)}
            >
              {snapshots.length} Snapshot{snapshots.length === 1 ? "" : "s"}
            </button>
          )}
          <div className="powerbi-hero-badge">
            <DashboardIcon />
            <span>Interactive Report</span>
          </div>
          {showSnapshotTray && snapshots.length > 0 && (
            <div className="analytics-snapshot-tray system-hero-snapshot-tray">
              {snapshots.map((snapshot, index) => (
                <div className="analytics-snapshot-pill" key={snapshot.id}>
                  <span>Snapshot {index + 1}</span>
                  <small>{snapshot.capturedAt}</small>
                  <button
                    type="button"
                    onClick={() => handleRemoveSnapshot(snapshot.id)}
                    aria-label={`Remove snapshot ${index + 1}`}
                  >
                    <MdDelete />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="powerbi-fullpage-frame" ref={dashboardFrameRef}>
        <iframe
          title="System Reports Power BI Dashboard"
          src={systemReportUrl}
          frameBorder="0"
          allowFullScreen
        />
      </div>

    </div>
  );
}

export default SystemReports;
